/**
 * ReorderPage — the reorder dashboard. For every catalog reagent it resolves the on-hand vs seuil
 * bucket (OK | BAS | RUPTURE), surfaces KPI tiles + a Recharts distribution bar, and a sortable table
 * with the bucket chip (color + glyph + text). Drives the full 5-state DoD.
 *
 * Data: GET /inventory/reactifs (the join root) + a batched GET /reactifs/:id/reorder per reagent.
 */
import { useMemo } from "react";
import { Bar, BarChart, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import {
  Card,
  ErrorState,
  ForbiddenState,
  LoadingState,
  OfflineState,
  Table,
  type Column,
} from "@/ui";
import { useCan } from "@/auth";
import { useT, useUiStore } from "@/app";
import { INV_CAPS } from "../caps";
import { useLabId, useReactifs, useReorderBatch, type ReorderRow } from "../api";
import type { ReorderBucket } from "../types";
import { idStr } from "../types";
import { fmtQty, BUCKET_TOKEN } from "../lib";
import { PageHeader } from "../components/PageHeader";
import { BucketBadge } from "../components/StatusBadge";

const BUCKET_ORDER: Record<ReorderBucket, number> = { RUPTURE: 0, BAS: 1, OK: 2 };

export function ReorderPage() {
  const { t } = useT();
  const labId = useLabId();
  const online = useUiStore((s) => s.online);
  const canRead = useCan(INV_CAPS.reactifRead);

  const catalogQuery = useReactifs(labId, canRead && online);
  const reactifs = useMemo(() => catalogQuery.data ?? [], [catalogQuery.data]);
  const rows = useReorderBatch(labId, reactifs, canRead && online && reactifs.length > 0);

  const counts = useMemo(() => {
    const c = { OK: 0, BAS: 0, RUPTURE: 0 };
    for (const r of rows) if (r.reorder) c[r.reorder.bucket] += 1;
    return c;
  }, [rows]);

  const chartData = useMemo(
    () =>
      (["RUPTURE", "BAS", "OK"] as ReorderBucket[]).map((b) => ({
        bucket: b,
        label: t(`inventory.reorder.bucket.${b}`),
        value: counts[b],
      })),
    [counts, t],
  );

  const sortedRows = useMemo(
    () =>
      [...rows].sort((a, b) => {
        const ba = a.reorder ? BUCKET_ORDER[a.reorder.bucket] : 99;
        const bb = b.reorder ? BUCKET_ORDER[b.reorder.bucket] : 99;
        return ba - bb || a.reactif.code.localeCompare(b.reactif.code);
      }),
    [rows],
  );

  const anyLoading = rows.some((r) => r.isLoading);

  const columns: ReadonlyArray<Column<ReorderRow>> = [
    { key: "code", header: t("inventory.reorder.col.code"), mono: true, sortable: true, sortAccessor: (r) => r.reactif.code, cell: (r) => r.reactif.code },
    { key: "libelle", header: t("inventory.reorder.col.libelle"), cell: (r) => r.reactif.libelle },
    {
      key: "onHand",
      header: t("inventory.reorder.col.onHand"),
      mono: true,
      align: "end",
      sortable: true,
      sortAccessor: (r) => r.reorder?.on_hand ?? -1,
      cell: (r) => <span className="nv-ltr-island">{r.reorder ? fmtQty(r.reorder.on_hand) : "…"}</span>,
    },
    {
      key: "seuil",
      header: t("inventory.reorder.col.seuil"),
      mono: true,
      align: "end",
      cell: (r) => <span className="nv-ltr-island">{r.reorder ? fmtQty(r.reorder.seuil) : "…"}</span>,
    },
    {
      key: "bucket",
      header: t("inventory.reorder.col.bucket"),
      cell: (r) =>
        r.reorder ? <BucketBadge bucket={r.reorder.bucket} /> : <span style={{ color: "var(--nv-steel)" }}>…</span>,
    },
  ];

  const header = <PageHeader title={t("inventory.reorder.title")} subtitle={t("inventory.reorder.subtitle")} />;

  if (!canRead) return <ForbiddenState requiredCapabilities={[INV_CAPS.reactifRead]} />;

  if (!online) {
    return (
      <Screen>
        {header}
        <OfflineState onRetry={() => void catalogQuery.refetch()} cached={reactifs.length > 0} />
      </Screen>
    );
  }
  if (catalogQuery.isLoading) {
    return (
      <Screen>
        {header}
        <LoadingState variant="table" rows={6} />
      </Screen>
    );
  }
  if (catalogQuery.isError) {
    return (
      <Screen>
        {header}
        <ErrorState
          message={catalogQuery.error.message}
          code={catalogQuery.error.code}
          onRetry={() => void catalogQuery.refetch()}
        />
      </Screen>
    );
  }
  if (reactifs.length === 0) {
    return (
      <Screen>
        {header}
        <Card>
          <div style={{ textAlign: "center", color: "var(--nv-slate)", padding: "var(--nv-s-5)" }}>
            <div style={{ fontSize: "var(--nv-fs-h2)" }}>{t("inventory.reorder.empty.title")}</div>
            <p>{t("inventory.reorder.empty.desc")}</p>
          </div>
        </Card>
      </Screen>
    );
  }

  return (
    <Screen>
      {header}

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
          gap: "var(--nv-s-3)",
        }}
      >
        <Kpi label={t("inventory.reorder.kpi.total")} value={reactifs.length} />
        <Kpi label={t("inventory.reorder.kpi.OK")} value={counts.OK} tokenColor="var(--nv-success)" />
        <Kpi label={t("inventory.reorder.kpi.BAS")} value={counts.BAS} tokenColor="var(--nv-warn)" />
        <Kpi label={t("inventory.reorder.kpi.RUPTURE")} value={counts.RUPTURE} tokenColor="var(--nv-danger)" />
      </div>

      <Card title={t("inventory.reorder.chart.title")}>
        {counts.OK + counts.BAS + counts.RUPTURE === 0 ? (
          <p style={{ color: "var(--nv-slate)", margin: 0 }}>{t("inventory.reorder.chart.empty")}</p>
        ) : (
          <div style={{ width: "100%", height: 220 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
                <XAxis dataKey="label" stroke="var(--nv-slate)" fontSize={12} tickLine={false} />
                <YAxis allowDecimals={false} stroke="var(--nv-slate)" fontSize={12} width={28} />
                <Tooltip
                  cursor={{ fill: "var(--nv-surface-2)" }}
                  contentStyle={{
                    background: "var(--nv-surface)",
                    border: "1px solid var(--nv-line)",
                    borderRadius: "var(--nv-r-sm)",
                    color: "var(--nv-ink)",
                  }}
                />
                <Bar dataKey="value" radius={[4, 4, 0, 0]} isAnimationActive={false}>
                  {chartData.map((d) => (
                    <Cell key={d.bucket} fill={BUCKET_TOKEN[d.bucket]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </Card>

      <Card padded={false}>
        <div style={{ padding: "var(--nv-s-3)" }}>
          {anyLoading && (
            <div style={{ fontSize: "var(--nv-fs-caption)", color: "var(--nv-steel)", marginBottom: "var(--nv-s-2)" }}>
              {t("inventory.reorder.loading")}
            </div>
          )}
          <Table
            columns={columns}
            rows={sortedRows}
            rowKey={(r) => idStr(r.reactif.id)}
            caption={t("inventory.reorder.title")}
            pageSize={15}
          />
        </div>
      </Card>
    </Screen>
  );
}

function Kpi({ label, value, tokenColor }: { label: string; value: number; tokenColor?: string }) {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "var(--nv-s-1)",
        padding: "var(--nv-s-3)",
        borderRadius: "var(--nv-r-md)",
        background: "var(--nv-surface-2)",
        border: "1px solid var(--nv-line-faint)",
      }}
    >
      <span style={{ fontSize: "var(--nv-fs-label)", color: "var(--nv-slate)" }}>{label}</span>
      <span
        className="nv-ltr-island"
        style={{ fontSize: "var(--nv-fs-h2)", fontWeight: 600, color: tokenColor ?? "var(--nv-ink)" }}
      >
        {value}
      </span>
    </div>
  );
}

function Screen({ children }: { children: React.ReactNode }) {
  return <div style={{ display: "flex", flexDirection: "column", gap: "var(--nv-s-5)" }}>{children}</div>;
}
